/**
 * Utilitário para gerenciar o acesso ao localStorage
 * Fornece métodos seguros para acessar, definir e remover dados
 */

class StorageManager {
  /**
   * Obtém um item do localStorage
   * @param {string} key - Chave do item
   * @param {*} defaultValue - Valor padrão se o item não existir
   * @returns {*} Valor do item ou defaultValue
   */
  static get(key, defaultValue = null) {
    try {
      const item = localStorage.getItem(key);
      if (item === null) {
        return defaultValue;
      }

      // Tenta fazer parse do JSON automaticamente
      try {
        return JSON.parse(item);
      } catch {
        // Se não for JSON válido, retorna como string
        return item;
      }
    } catch (error) {
      console.error(`Erro ao acessar localStorage para chave "${key}":`, error);
      return defaultValue;
    }
  }

  /**
   * Define um item no localStorage
   * @param {string} key - Chave do item
   * @param {*} value - Valor a ser armazenado
   * @returns {boolean} true se foi bem-sucedido, false caso contrário
   */
  static set(key, value) {
    try {
      const serializedValue =
        typeof value === 'string' ? value : JSON.stringify(value);
      localStorage.setItem(key, serializedValue);
      return true;
    } catch (error) {
      console.error(`Erro ao definir localStorage para chave "${key}":`, error);
      return false;
    }
  }

  /**
   * Remove um item do localStorage
   * @param {string} key - Chave do item a ser removido
   * @returns {boolean} true se foi bem-sucedido, false caso contrário
   */
  static remove(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Erro ao remover localStorage para chave "${key}":`, error);
      return false;
    }
  }

  /**
   * Limpa todo o localStorage
   * @returns {boolean} true se foi bem-sucedido, false caso contrário
   */
  static clear() {
    try {
      localStorage.clear();
      return true;
    } catch (error) {
      console.error('Erro ao limpar localStorage:', error);
      return false;
    }
  }

  /**
   * Verifica se uma chave existe no localStorage
   * @param {string} key - Chave a ser verificada
   * @returns {boolean} true se a chave existe, false caso contrário
   */
  static has(key) {
    try {
      return localStorage.getItem(key) !== null;
    } catch (error) {
      console.error(
        `Erro ao verificar localStorage para chave "${key}":`,
        error,
      );
      return false;
    }
  }

  /**
   * Obtém o tamanho do localStorage
   * @returns {number} Número de itens no localStorage
   */
  static size() {
    try {
      return localStorage.length;
    } catch (error) {
      console.error('Erro ao obter tamanho do localStorage:', error);
      return 0;
    }
  }

  /**
   * Obtém todas as chaves do localStorage
   * @returns {string[]} Array com todas as chaves
   */
  static keys() {
    try {
      return Object.keys(localStorage);
    } catch (error) {
      console.error('Erro ao obter chaves do localStorage:', error);
      return [];
    }
  }

  /**
   * Obtém múltiplos itens do localStorage
   * @param {string[]} keys - Array de chaves
   * @returns {Object} Objeto com chave-valor dos itens encontrados
   */
  static getMultiple(keys) {
    const result = {};
    keys.forEach((key) => {
      result[key] = this.get(key);
    });
    return result;
  }

  /**
   * Define múltiplos itens no localStorage
   * @param {Object} items - Objeto com chave-valor
   * @returns {boolean} true se todos foram definidos com sucesso
   */
  static setMultiple(items) {
    try {
      Object.entries(items).forEach(([key, value]) => {
        this.set(key, value);
      });
      return true;
    } catch (error) {
      console.error('Erro ao definir múltiplos itens no localStorage:', error);
      return false;
    }
  }
}

// Exporta a classe e também funções individuais para uso mais conveniente
export default StorageManager;

// Funções individuais para uso direto
export const getStorage = StorageManager.get;
export const setStorage = StorageManager.set;
export const removeStorage = StorageManager.remove;
export const clearStorage = StorageManager.clear;
export const hasStorage = StorageManager.has;
export const getStorageSize = StorageManager.size;
export const getStorageKeys = StorageManager.keys;
export const getMultipleStorage = StorageManager.getMultiple;
export const setMultipleStorage = StorageManager.setMultiple;
